/*
 * QUEST SOFTWARE PROPRIETARY INFORMATION
 *
 * This software is confidential.  Quest Software Inc., or one of its
 * subsidiaries, has supplied this software to you under terms of a
 * license agreement, nondisclosure agreement or both.
 *
 * You may not copy, disclose, or use this software except in accordance with
 * those terms.
 *
 *
 * Copyright 2026 Quest Software Inc.
 * ALL RIGHTS RESERVED.
 *
 * QUEST SOFTWARE INC. MAKES NO REPRESENTATIONS OR
 * WARRANTIES ABOUT THE SUITABILITY OF THE SOFTWARE,
 * EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED
 * TO THE IMPLIED WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE, OR
 * NON-INFRINGEMENT.  QUEST SOFTWARE SHALL NOT BE
 * LIABLE FOR ANY DAMAGES SUFFERED BY LICENSEE
 * AS A RESULT OF USING, MODIFYING OR DISTRIBUTING
 * THIS SOFTWARE OR ITS DERIVATIVES.
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { SidebarService } from '../../services/sidebar.service';
import { MultiRouterPages, PagesNotInLeftMenu } from '../../models/nav-item';
import { cloneDeep } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  Router,
  Event,
  NavigationEnd,
  ActivatedRoute,
  NavigationStart,
} from '@angular/router';
import { environment } from 'src/environments/environment';
import { sidebarIcons } from '../../config/sidebar-icons-mapping';
import { Title } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { AddBookmarkDialogComponent } from './../add-bookmark-dialog/add-bookmark-dialog.component';
import { FacSnackbarOptions, SnackbarService } from '@foglight/angular-common';
import { Store } from '@ngrx/store';
import { isCloudFlagSelector } from 'src/app/store/app.selectors';
import { from, SubscriptionLike, of } from 'rxjs';
import { AppInfoService, PopupDialogService } from '@foglight/angular-common';
import { HomePageDialogInputData } from '../../models/home-page-dialog-input-data';
import { Location } from '@angular/common';
import { NavigationMenuItem } from '../../models/navigation-menu-item';

const WELCOME_PAGE_URL = '/home';

@Component({
  selector: 'fc-nav-menu-panel',
  templateUrl: './nav-menu-panel.component.html',
  styleUrls: ['./nav-menu-panel.component.scss'],
})
export class NavigationMenuPanelComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private locationSub!: SubscriptionLike;
  @Input() isLoading!: boolean;
  @Input() isError = false;
  @Input() isPinned!: boolean;
  @Output() pinClick = new EventEmitter();
  @Output() editModeClick = new EventEmitter();
  @Output() tooltipHover = new EventEmitter();

  private homePageDilogComponent$ = from(
    import('../home-page-dialog/home-page-dialog.component').then(
      (component) => component.HomePageDialogComponent
    )
  );

  isLargeScreen = false;
  classicFgPath = '/console';
  configurationItems: NavigationMenuItem[] = [];
  showConfigMenu = false;
  navItems: NavigationMenuItem[] = [];
  bookmarksItem!: NavigationMenuItem;
  bookmarkUrl = '';
  selectedTabIndex!: number;
  navItem = '';
  currentNavItem!: NavigationMenuItem;
  isCloudFlag = false;
  selectedNavItem!: NavigationMenuItem | undefined;
  homePageNavItem!: NavigationMenuItem;

  foldedState$;

  welcomePageNavItem: NavigationMenuItem = {
    id: 'home',
    displayName: 'Home',
    link: 'wcf?name=general-view-aui-wrapper&viewId=system:administration_home.159',
    visible: true,
    type: 'WCF',
    description: '',
    iconName: '',
  };

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private titleService: Title,
    private dialog: MatDialog,
    private ref: ChangeDetectorRef,
    private snackbarService: SnackbarService,
    private store: Store,
    private route: ActivatedRoute,
    private popupDialogService: PopupDialogService,
    private appInfoService: AppInfoService,
    private location: Location
  ) {
    this.foldedState$ = this.sidebarService.foldedState$;
  }

  @HostListener('window:resize')
  onResize() {
    this.checkForScreenResolution();
  }

  ngOnInit() {
    this.store.select(isCloudFlagSelector).pipe(takeUntil(this.destroy$)).subscribe((isCloud) => {
      isCloud
        ? localStorage.setItem('isCloud', 'true')
        : localStorage.setItem('isCloud', 'false');
      this.isCloudFlag = isCloud;
      this.hideFeeds();
      this.ref.detectChanges();
    });

    const isPinned = sessionStorage.getItem('isPinned') || '';

    if (isPinned) {
      sessionStorage.setItem('isPinned', isPinned);
    } else {
      sessionStorage.setItem('isPinned', '' + false);
    }

    this.sidebarService.navItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.navItems = cloneDeep(items);

        if (this.isCloudFlag) {
          this.navItems = this.filterOutNavItem(this.navItems, "What's New");
          this.ref.detectChanges();
        }

        this.setSidebarItemsIcons();
        this.setSelectedNavItemFromSessionStorage();

        const url = this.router.routerState.snapshot.url;
        let link = url.split('/')[1] || '';

        if (MultiRouterPages.some((page) => url.includes(page))) {
          link = url.split('/')[2];
          if (link.includes('?')) {
            link = link.substring(0, link.indexOf('?'));
          }
        }

        if (
          url !== '/' &&
          link !==
            'wcf?name=general-view-aui-wrapper&viewId=system:administration_home.159'
        ) {
          let id = '';
          let filteredNavItems = [];
          if (url.includes('wcf?')) {
            filteredNavItems = this.filterItemsByLink(link, this.navItems);
            id = filteredNavItems[0]?.hasOwnProperty('id')
              ? filteredNavItems[0].id
              : '';
          } else if (url.includes('foglight-sql-profiler/query-insights')) {
            filteredNavItems = this.filterItemsByLink(
              'foglight-sql-profiler/query-insights',
              this.navItems
            );
            id = filteredNavItems[0]?.hasOwnProperty('id')
              ? filteredNavItems[0].id
              : '';
          } else {
            if (link.includes('?')) {
              link = link.substring(0, link.indexOf('?'));
            }

            id =
              link.indexOf('-aui-') != -1 ? link.replace('-aui-', ':') : link;
            filteredNavItems = this.filterItemsById(id, this.navItems);
          }

          filteredNavItems.forEach((item) => {
            if (item.id === id) {
              this.currentNavItem = item;
              this.navItem = this.currentNavItem?.displayName || '';
            }
          });

          this.bookmarkUrl = url;
          let title = this.currentNavItem?.displayName || '';

          if (title !== '') {
            this.titleService.setTitle(`${title} - Foglight`);
          }
          if (url.indexOf(WELCOME_PAGE_URL) != -1) {
            this.titleService.setTitle(`Welcome - Foglight`);
          }
          if (this.currentNavItem?.link && link.indexOf('-aui-') == -1) {
            let alarmTemplateId = sessionStorage.getItem('alarmTemplateId');
            if (!alarmTemplateId) {
              if (url.indexOf('wcf') === -1)
                this.router.navigate([this.currentNavItem?.link], {
                  relativeTo: this.route,
                  queryParamsHandling: 'merge',
                });
              else this.router.navigateByUrl(this.currentNavItem?.link);
              this.sidebarService.selectedNavItem.next(true);
            }
          }
        }

        this.setHomePageNavItemfromLocalStorage();
      });

    this.sidebarService.bookmarksItem$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        //removed undefined data on delete action.
        if (this.bookmarksItem && (!this.bookmarksItem.children || !this.bookmarksItem.children[0])) {
          this.bookmarksItem.children = this.bookmarksItem.children?.filter(Boolean) ?? [];
          return;
        }
        if (!this.bookmarksItem) {
          this.bookmarksItem = cloneDeep(items);
        } else {
          const newBookmark = items?.children?.[0];
          if (newBookmark) {
            const existingChildren = this.bookmarksItem.children ?? [];
            !existingChildren.includes(newBookmark) || existingChildren.length === 0
              ? existingChildren.push(newBookmark)
              : null;
            this.bookmarksItem.children = existingChildren;
          }
        }
        this.bookmarksItem.iconName = 'fg-bookmarks';
        this.bookmarksItem.children = this.bookmarksItem.children ?? [];
        this.bookmarksItem.children.forEach((bookmarkItem) => {
          bookmarkItem.tooltip =
            'This bookmark was created using the classic web console, please navigate to the classic web console to remove it';
          bookmarkItem.source = 'console';
        });
        this.sidebarService.bookmarkAdded.next(true);
      });

    this.sidebarService.configItems$
      .pipe(takeUntil(this.destroy$))
      .subscribe((items) => {
        this.configurationItems = cloneDeep(items);
        this.hideFeeds();
      });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
      if (event instanceof NavigationStart) {
        this.appInfoService.emitNavRouterItemsUpdate(event);
      }
    });

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.appInfoService.emitNavRouterItemsUpdate(event);
        const url = this.router.routerState.snapshot.url;
        let link = url.split('/')[1];

        if (MultiRouterPages.some((page) => event.url.includes(page))) {
          link = event.url.split('/')[2];
          if (link.includes('?')) {
            link = link.substring(0, link.indexOf('?'));
          }
        }

        if (
          link !==
          'wcf?name=general-view-aui-wrapper&viewId=system:administration_home.159'
        ) {
          let id = '';
          let filteredNavItems = [];
          if (url.includes('wcf?')) {
            filteredNavItems = this.filterItemsByLink(link, this.navItems);
            id = filteredNavItems[0]?.id;
            this.setSelectedNavItemFromId(id);
          } else {
            if (link.includes('?')) {
              link = link.substring(0, link.indexOf('?'));
            }
            this.setSelectedNavItemFromLink(link);
            id = link;
            filteredNavItems = this.filterItemsById(id, this.navItems);
          }
          filteredNavItems.forEach((item) => {
            if (item.id === id) {
              this.navItem = item.displayName || '';
            }
          });
          this.bookmarkUrl = url;
        }
      }
    });

    this.sidebarService.deletedBookmark
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        this.bookmarksItem.children = this.bookmarksItem.children?.filter(
          (bookmark) => bookmark.id !== id
        );
        this.sidebarService.updateBooksmarksItem(this.bookmarksItem);
        this.ref.detectChanges();
      });

    this.addHomePageNavItemChangeSubscription();
    this.addCurrentSelectedIdChangeSubscription();

    this.locationSub = this.location.subscribe((event) => {
      const pagesNotinLeftMenu = PagesNotInLeftMenu.some((page) =>
        event.url?.includes(page)
      );
      if (pagesNotinLeftMenu) {
        this.selectedNavItem = undefined;
      }
      if (event.url === WELCOME_PAGE_URL) {
        this.selectedNavItem = cloneDeep(this.welcomePageNavItem);
      }
    });

    this.checkForScreenResolution();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.locationSub) {
      this.locationSub.unsubscribe();
    }
    this.sidebarService.updateNavItems(this.navItems);
  }

  filterOutNavItem(
    navItems: NavigationMenuItem[],
    targetName: string
  ): NavigationMenuItem[] {
    const filteredItems: NavigationMenuItem[] = [];

    for (const item of navItems) {
      if (item.displayName !== targetName) {
        if (item.children && item.children.length > 0) {
          item.children = this.filterOutNavItem(item.children, targetName);
          filteredItems.push(item);
        } else {
          filteredItems.push(item);
        }
      }
    }

    return filteredItems;
  }

  checkForScreenResolution() {
    let screenWidth = window.innerWidth;
    if (screenWidth >= 1900) {
      this.isLargeScreen = true;
    } else {
      this.isLargeScreen = false;
    }
    this.ref.markForCheck();
  }

  addHomePageNavItemChangeSubscription() {
    this.sidebarService.homePageNavItem
      .pipe(takeUntil(this.destroy$))
      .subscribe((item: NavigationMenuItem) => {
        this.homePageNavItem = item;
        let homePageNavItemInMenu = this.sidebarService.findNavItemBylink(
          cloneDeep(this.navItems),
          this.homePageNavItem.link || ''
        );

        if (homePageNavItemInMenu) {
          this.homePageNavItem = homePageNavItemInMenu;
        }

        const homePageToStore = {
          displayName: this.homePageNavItem.displayName,
          id: this.homePageNavItem.id,
          link: this.homePageNavItem.link,
          type: this.homePageNavItem.type,
          visible: this.homePageNavItem.visible,
        };

        localStorage.setItem('homePage', JSON.stringify(homePageToStore));
        this.ref.markForCheck();
      });
  }

  addCurrentSelectedIdChangeSubscription() {
    this.sidebarService.currentSelectedId
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedMenuItemId) => {
        this.selectedNavItem = this.sidebarService.findNavItemById(
          cloneDeep(this.navItems),
          selectedMenuItemId
        );
        this.ref.markForCheck();
      });
  }

  setHomePageNavItemfromLocalStorage() {
    try {
      let homePage = localStorage.getItem('homePage');
      this.homePageNavItem = JSON.parse(homePage || '{}');
      let homePageNavItemInMenu = this.sidebarService.findNavItemBylink(
        cloneDeep(this.navItems),
        this.homePageNavItem.link || ''
      );

      if (homePageNavItemInMenu) {
        this.homePageNavItem = homePageNavItemInMenu;
      }

      this.ref.markForCheck();
    } catch (err) {
      console.error(err);
    }
  }

  setSelectedNavItemFromSessionStorage() {
    try {
      let selectedMenuItemId = sessionStorage.getItem('selectedItem') || '';

      this.selectedNavItem = this.sidebarService.findNavItemById(
        cloneDeep(this.navItems),
        selectedMenuItemId
      );

      this.ref.markForCheck();
    } catch (err) {
      console.error(err);
    }
  }

  setSelectedNavItemFromId(selectedMenuItemId: string): void {
    try {
      this.selectedNavItem = this.sidebarService.findNavItemById(
        cloneDeep(this.navItems),
        selectedMenuItemId
      );

      this.ref.markForCheck();
    } catch (err) {
      console.error(err);
    }
  }

  setSelectedNavItemFromLink(link: string): void {
    try {
      this.selectedNavItem = this.sidebarService.findNavItemBylink(
        cloneDeep(this.navItems),
        link
      );

      this.ref.markForCheck();
    } catch (err) {
      console.error(err);
    }
  }

  private replaceViewIdForWcfBookmark(urlSuffix: string): string {
    let viewInfo = JSON.parse(sessionStorage.getItem('viewInfo') || 'null');

    const [path, queryString] = urlSuffix.split('?');
    const searchParams = new URLSearchParams(queryString);

    if (viewInfo) {
      let viewIdValue = viewInfo['viewId'] || '';
      searchParams.set('viewId', viewIdValue);
    }

    return path + '?' + searchParams.toString();
  }

  hideFeeds(): void {
    if (this.isCloudFlag) {
      this.configurationItems = this.configurationItems.filter((item) => {
        return item.displayName !== 'Feeds';
      });
    }
  }

  setSidebarItemsIcons(): void {
    if (this.navItems) {
      const icons = sidebarIcons;

      this.navItems.forEach((item) => {
        const itemId = item.id.replace('_', '-');
        item.iconName = icons[itemId] ? icons[itemId] : icons['system:default'];
      });
    }
  }

  filterItemsById(searchData: string, target: NavigationMenuItem[], accum: NavigationMenuItem[] = []): NavigationMenuItem[] {
    target.forEach((f) => {
      if (f.children) {
        this.filterItemsById(searchData, f.children, accum);
      }
      if (f.id.includes(searchData)) {
        accum.push(f);
      }
    });

    return accum;
  }

  filterItemsByLink(searchData: string, target: NavigationMenuItem[], accum: NavigationMenuItem[] = []): NavigationMenuItem[] {
    target.forEach((navigationMenuItem) => {
      if (navigationMenuItem.children) {
        this.filterItemsByLink(searchData, navigationMenuItem.children, accum);
      }

      if (navigationMenuItem.link === searchData) {
        accum.push(navigationMenuItem);
      }
    });

    return accum;
  }

  pinClicked(): void {
    this.pinClick.emit();
  }

  toggleConfigMenu(event: any): void {
    if (event.toElement) {
      if (
        event.toElement.nodeName !== 'MAT-TOOLTIP-COMPONENT' &&
        event.toElement.classList[0] !== 'mat-tooltip'
      ) {
        this.showConfigMenu = !this.showConfigMenu;
      } else {
        this.tooltipHover.emit();
      }
    } else {
      this.showConfigMenu = !this.showConfigMenu;
    }
  }

  isDisabled(e: any): boolean {
    return e.scrollWidth <= e.clientWidth;
  }

  onErrorPageRefresh(): void {
    location.reload();
  }

  onAddBookmark(): void {
    if (this.bookmarkUrl.includes('wcf?')) {
      this.bookmarkUrl = this.replaceViewIdForWcfBookmark(this.bookmarkUrl);
    }

    this.popupDialogService
      .openPopupDialog({
        class: 'fc-add-edit-bookmark-panel',
        height: 'auto',
        width: '505px',
        inputData: {
          name: this.navItem,
          link: this.bookmarkUrl,
        },
        disableClose: true,
        dynamicComponents: of(AddBookmarkDialogComponent),
        hasBackdrop: true,
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response) {
          if (response.message === 'success') {
            this.showSnackbar();
            let newBookmark: NavigationMenuItem = {
              children: this.bookmarksItem?.children || [],
              description: undefined,
              displayName: 'Bookmarks',
              id: 'dummy-parent',
              link: undefined,
              type: 'WCF',
              visible: true,
              iconName: undefined,
            };

            newBookmark.children?.push(response.bookmark);

            this.sidebarService.updateBooksmarksItem(newBookmark);
            this.ref.detectChanges();
            if (response.email) {
              let url = this.router.routerState.snapshot.url;
              if (url.includes('wcf')) {
                url = encodeURIComponent(url);
              }
              window.location.href = `mailto:?subject=${response.bookmark.displayName}&body=${document.location.origin}/aui${url}`;
            }
            setTimeout(() => {
              this.sidebarService.sidebarWidth$.next(
                parseInt(
                  this.sidebarService.sidebarWidth$.value.slice(
                    0,
                    this.sidebarService.sidebarWidth$.value.indexOf('p')
                  )
                ) -
                  1 +
                  'px'
              );
              this.sidebarService.sidebarWidth$.next(
                parseInt(
                  this.sidebarService.sidebarWidth$.value.slice(
                    0,
                    this.sidebarService.sidebarWidth$.value.indexOf('p')
                  )
                ) +
                  1 +
                  'px'
              );
            }, 1);
            this.ref.detectChanges();
          }
        }
      });
  }

  onEditModeClick(): void {
    this.editModeClick.emit();
  }

  openClassicFoglight(): void {
    window.open(environment.foglightBaseUrl.concat(this.classicFgPath));
  }

  onItemClick(item: NavigationMenuItem, event?: MouseEvent): void {
    if (item.link) {
      this.titleService.setTitle(`${item.displayName} - Foglight`);
      this.router.navigateByUrl(item.link);
    }
  }

  submit(type: 'set' | 'reset'): void {
    let inputData: HomePageDialogInputData = {
      homePageNavItem: cloneDeep(this.homePageNavItem),
      selectedNavItem: cloneDeep(this.selectedNavItem!),
      type: type,
    };

    let options = {
      height: this.isLargeScreen ? '250px' : '213px',
      width: this.isLargeScreen ? '500px' : '400px',
      disableClose: false,
      dynamicComponents: this.homePageDilogComponent$,
      inputData: inputData,
      class: 'fac-mat-popup-dialog-container-override',
    };

    this.popupDialogService.openPopupDialog(options);
  }

  showSnackbar(): void {
    const options: FacSnackbarOptions = {
      horizontalPosition: 'end',
      verticalPosition: 'top',
      duration: 3000,
      data: {
        type: 'success',
        text: 'Bookmark was created',
      },
    };

    this.snackbarService.show(options);
  }
}
